// ===================================================================
// PA GRANTS — Cross-Project Document Library
// ===================================================================

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Modal, ActionIcon, Loader, Center,
} from '../../components/gab/GabComponents';
import { IconPlus, IconSearch, IconDownload, IconTrash, IconUpload, IconFile, IconFileDescription, IconRefresh } from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { notifications } from '../../lib/notifications';
import { GabDataTable } from '../../components/gab';

const DOC_TYPE_OPTIONS = [
  { value: 'Contract', label: 'Contract' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Photo', label: 'Photo' },
  { value: 'Report', label: 'Report' },
  { value: 'Form', label: 'Form' },
  { value: 'Correspondence', label: 'Correspondence' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Other', label: 'Other' },
];

export default function Documents() {
  const {
    documents, fetchDocuments, createDocument, deleteDocument,
    uploadDocumentFile, getDocumentDownloadUrl,
    projects, fetchProjects,
  } = usePAGrantsStore();

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ title: '', documentType: 'Other', uploadedBy: '' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    await Promise.all([fetchDocuments(), fetchProjects()]);
    setLoading(false);
  }

  const projectMap = useMemo(() => {
    const m: Record<number, string> = {};
    projects.forEach((p: any) => { m[p.Rid] = p.projectNumber || p.title || `Project ${p.Rid}`; });
    return m;
  }, [projects]);

  const filtered = useMemo(() => {
    let list = documents as any[];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.documentType || '').toLowerCase().includes(q) ||
        (d.uploadedBy || '').toLowerCase().includes(q)
      );
    }
    if (typeFilter) list = list.filter(d => d.documentType === typeFilter);
    return list;
  }, [documents, search, typeFilter]);

  async function handleCreate() {
    if (!form.title) { notifications.show({ message: 'Title required', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      const id = await createDocument({ title: form.title, documentType: form.documentType, uploadedBy: form.uploadedBy });
      if (id && selectedFile) {
        await uploadDocumentFile(id, selectedFile);
      }
      notifications.show({ message: 'Document added', color: 'var(--color-success)' });
      setModalOpen(false);
      setForm({ title: '', documentType: 'Other', uploadedBy: '' });
      setSelectedFile(null);
    } catch {
      notifications.show({ message: 'Failed to save', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  async function handleDownload(doc: any) {
    if (!doc.fileId) { notifications.show({ message: 'No file attached', color: '#F86A0B' }); return; }
    setDownloading(doc.Rid);
    try {
      const url = await getDocumentDownloadUrl(doc.fileId);
      if (url) { window.open(url, '_blank'); }
      else { notifications.show({ message: 'Download URL not available', color: '#F86A0B' }); }
    } catch {
      notifications.show({ message: 'Download failed', color: 'var(--color-error)' });
    }
    setDownloading(null);
  }

  async function handleDelete(doc: any) {
    if (!confirm(`Delete "${doc.title || 'document'}"?`)) return;
    await deleteDocument(doc.Rid);
    notifications.show({ message: 'Deleted', color: '#939598' });
  }

  const columns = [
    {
      key: 'title', label: 'Document', sortable: true,
      render: (r: any) => (
        <Group gap="xs" wrap="nowrap">
          <IconFileDescription size={16} color="var(--color-primary-700)" />
          <Stack gap={0}>
            <Text size="sm" fw={500}>{r.title || r.documentName || 'Untitled'}</Text>
            {r.uploadedBy && <Text size="xs" c="dimmed">by {r.uploadedBy}</Text>}
          </Stack>
        </Group>
      ),
    },
    {
      key: 'documentType', label: 'Type', width: 130,
      render: (r: any) => r.documentType ? <Badge variant="light" size="sm">{r.documentType}</Badge> : <Text size="xs" c="dimmed">—</Text>,
    },
    {
      key: 'projectId', label: 'Project', width: 160,
      render: (r: any) => r.projectId ? (
        <Text size="sm">{projectMap[r.projectId] || `#${r.projectId}`}</Text>
      ) : <Text size="xs" c="dimmed">—</Text>,
    },
    {
      key: 'tags', label: 'Tags', width: 130,
      render: (r: any) => r.tags ? <Badge variant="outline" size="xs">{r.tags}</Badge> : null,
    },
    {
      key: 'actions', label: '', width: 80,
      render: (r: any) => (
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <ActionIcon
            variant="subtle" size="sm" color="var(--color-primary-700)"
            loading={downloading === r.Rid}
            title="Download"
            onClick={() => handleDownload(r)}
          >
            <IconDownload size={14} />
          </ActionIcon>
          <ActionIcon variant="subtle" size="sm" color="var(--color-error)" title="Delete" onClick={() => handleDelete(r)}>
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Documents</Title>
        <Button variant="primary" size="sm" leftIcon={<IconPlus size={14} />} onClick={() => setModalOpen(true)}>
          Add Document
        </Button>
      </Group>

      <Paper p="md" radius="md" withBorder>
        <Group gap="sm" mb="md">
          <TextInput
            placeholder="Search documents…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1 }}
            size="sm"
          />
          <Select
            placeholder="All types"
            data={DOC_TYPE_OPTIONS}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
            size="sm"
            style={{ width: 160 }}
          />
          <ActionIcon variant="subtle" size="md" onClick={load} title="Refresh">
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>

        {loading ? (
          <Center py="xl"><Loader /></Center>
        ) : (
          <GabDataTable
            columns={columns as any}
            data={filtered}
            rowKey={(r: any) => r.Rid}
            striped
            emptyMessage="No documents found."
          />
        )}
        <Text size="xs" c="dimmed" mt="sm">{filtered.length} document{filtered.length !== 1 ? 's' : ''}</Text>
      </Paper>

      {/* Add Document Modal */}
      <input ref={uploadInputRef} type="file" hidden onChange={e => setSelectedFile(e.target.files?.[0] || null)} />
      <Modal opened={modalOpen} onClose={() => { setModalOpen(false); setSelectedFile(null); }} title="Add Document">
        <Stack gap="sm">
          <TextInput
            label="Document Title"
            required
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Contract with ABC Debris Inc."
          />
          <Select
            label="Document Type"
            data={DOC_TYPE_OPTIONS}
            value={form.documentType}
            onChange={v => setForm(f => ({ ...f, documentType: v || 'Other' }))}
          />
          <TextInput
            label="Uploaded By"
            value={form.uploadedBy}
            onChange={e => setForm(f => ({ ...f, uploadedBy: e.target.value }))}
            placeholder="Name or position"
          />

          {/* File picker */}
          <Paper
            p="md" radius="sm"
            style={{ border: '2px dashed var(--color-primary-300)', textAlign: 'center', cursor: 'pointer' }}
            onClick={() => uploadInputRef.current?.click()}
          >
            <Stack align="center" gap="xs">
              <IconUpload size={24} color="var(--color-primary-700)" />
              {selectedFile
                ? <Text size="sm" fw={500}>{selectedFile.name}</Text>
                : <Text size="sm" c="dimmed">Click to attach a file (optional)</Text>
              }
            </Stack>
          </Paper>

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="ghost" onClick={() => { setModalOpen(false); setSelectedFile(null); }}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleCreate}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
